import { AlertDialog, BodyText, HeaderText, toast } from "@/components/ui";
import { formatDate, formatDateTime } from "@/lib/time";
import { Surgery, getDisplay } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { EditableRecordButton } from "../../components/EditableRecordButton";
import { useUpdateSurgicalHistory, useVoidSurgicalHistory } from "../network";
import { SurgicalHistoryForm } from "./SurgicalHistoryForm";

export const SurgicalHistoryItem = ({
  surgicalHistory,
}: { surgicalHistory: Surgery }) => {
  const { patientId } = useParams({ strict: false });
  const [editing, setEditing] = useState(false);

  const [showVoidPrompt, setShowVoidPrompt] = useState(false);

  const {
    mutate: updateSurgicalHistory,
    isPending: isUpdatingSurgicalHistory,
  } = useUpdateSurgicalHistory({
    surgicalHistoryId: surgicalHistory.id as string,
    patientId: patientId as string,
    onSuccess: () => {
      toast.success("Surgical history updated successfully");
      setEditing(false);
    },
  });

  const { mutate: voidSurgicalHistory, isPending: isVoidingSurgicalHistory } =
    useVoidSurgicalHistory({
      surgicalHistoryId: surgicalHistory.id as string,
      patientId: patientId as string,
      onSuccess: () => {
        toast.success("Surgical history voided successfully");
      },
    });

  return editing ? (
    <SurgicalHistoryForm
      surgicalHistory={surgicalHistory}
      onCancel={() => {
        setEditing(false);
      }}
      onSubmit={(data) => {
        updateSurgicalHistory({
          performedDate: data.performedDate.toISOString(),
          procedureCode: {
            coding: [
              {
                code: data.procedureCode,
                display: data.procedureCodeDisplay,
                system: data.procedureCodeSystem,
              },
            ],
            text: data.procedureCodeDisplay,
          },
        });
      }}
      isSubmitting={isUpdatingSurgicalHistory}
    />
  ) : (
    <EditableRecordButton
      onClick={() => {
        setEditing(true);
      }}
      onDelete={() => setShowVoidPrompt(true)}
      deletePending={isVoidingSurgicalHistory}
      containerClassName="w-full"
    >
      <div className="flex flex-row gap-2 w-full">
        <div className="flex flex-1 flex-col gap-1">
          <div>
            <HeaderText size="sm">Date</HeaderText>
          </div>
          <div className="flex flex-col gap-1">
            <BodyText size="sm">
              {formatDate(surgicalHistory.performedDateTime)}
            </BodyText>
            <BodyText size="xs" textColor="secondary">
              {surgicalHistory.recorder?.display} on{" "}
              {formatDateTime(
                Surgery.retrieveExtension(
                  surgicalHistory.extension,
                  Surgery.ExtensionUrl.values.RECORDED,
                  Surgery.FhirValueName.DATE_TIME,
                ),
              )}
            </BodyText>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <HeaderText size="sm">Type</HeaderText>
          <BodyText size="sm">
            {(surgicalHistory.code && getDisplay(surgicalHistory.code)) ||
              "Unknown"}
          </BodyText>
        </div>
      </div>
      <AlertDialog
        isOpen={showVoidPrompt}
        onOpenChange={setShowVoidPrompt}
        isControlled
        title="Void Surgical History"
        body={`Are you sure you want to delete this surgical history? This will remove it entirely from the patient's medical record, and should only be used if was added in error (i.e. the wrong patient).`}
        actionLabel="Void"
        cancelLabel="Cancel"
        onAction={() => {
          voidSurgicalHistory();
          setShowVoidPrompt(false);
        }}
        onCancel={() => {
          setShowVoidPrompt(false);
        }}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </EditableRecordButton>
  );
};
